import React from 'react';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import { heroSlides } from '../data/mockData';
import { ArrowDown } from 'lucide-react';
import { useLang } from '../context/LanguageContext';

const t = {
  fr: {
    h1: 'Créer des images qui donnent faim.',
    h1b: 'Et des histoires qui donnent envie de rester.',
    sub: 'Studio créatif dédié aux restaurants, hôtels et lieux d\'hospitalité.',
    tagline: 'Image · narration · expérience',
    cta1: 'Parlons de votre projet',
    cta2: 'Voir nos projets',
  },
  en: {
    h1: 'Creating images that make you hungry.',
    h1b: 'And stories that make you want to stay.',
    sub: 'Creative studio dedicated to restaurants, hotels and hospitality venues.',
    tagline: 'Image · narrative · experience',
    cta1: 'Let\'s talk about your project',
    cta2: 'See our work',
  },
};

const HeroSection = () => {
  const { lang } = useLang();
  const tx = t[lang];

  const scrollToContent = () => {
    const element = document.getElementById('about');
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="hero" className="relative h-screen w-full overflow-hidden">
      <Swiper
        modules={[Navigation, Pagination, Autoplay, EffectFade]}
        slidesPerView={1}
        spaceBetween={0}
        loop={true}
        autoplay={{ delay: 5000, disableOnInteraction: false, pauseOnMouseEnter: true }}
        effect="fade"
        fadeEffect={{ crossFade: true }}
        speed={1200}
        pagination={{ clickable: true, dynamicBullets: true }}
        className="h-full w-full hero-swiper"
      >
        {heroSlides.map((slide, index) => (
          <SwiperSlide key={slide.id}>
            <div className="relative h-full w-full grid grid-cols-1 md:grid-cols-2" style={{ backgroundColor: slide.bgColor }}>
              {/* Left Content */}
              <div className="relative flex flex-col justify-between p-6 md:p-12 lg:p-16 z-10">
                {/* Mobile Content */}
                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.8 }} className="md:hidden flex flex-col justify-center h-full text-center">
                  <h1 className="text-2xl sm:text-3xl font-semibold text-white mb-3 leading-tight">
                    {tx.h1}<br />{tx.h1b}
                  </h1>
                  <p className="text-sm sm:text-base text-white/80 mb-2 leading-relaxed max-w-md mx-auto">{tx.sub}</p>
                  <p className="text-xs text-white/60 mb-6 font-mono tracking-widest uppercase">{tx.tagline}</p>
                  <div className="flex flex-col gap-3">
                    <button onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })} className="cta-button bg-white text-[#1a3a52] hover:bg-[#f5e6d3] transition-all px-6 py-3 text-sm w-full">{tx.cta1}</button>
                    <button onClick={() => document.getElementById('portfolio').scrollIntoView({ behavior: 'smooth' })} className="cta-button bg-transparent border-2 border-white text-white hover:bg-white/10 transition-all px-6 py-3 text-sm w-full">{tx.cta2}</button>
                  </div>
                </motion.div>

                {/* Desktop Content */}
                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.8 }} className="hidden md:block space-y-4">
                  <h2 className="text-xl md:text-2xl font-normal" style={{ color: slide.textColor }}>{slide.title}</h2>
                  <p className="text-lg md:text-xl opacity-70" style={{ color: slide.textColor }}>{typeof slide.description === 'object' ? slide.description[lang] : slide.description}</p>
                  <div className="flex items-center gap-2 pt-3 flex-wrap">
                    {slide.categories.map((cat, idx) => (
                      <span key={idx} className="service-button px-4 py-2 rounded-full text-xs uppercase tracking-wider font-mono" style={{ backgroundColor: idx === 0 ? '#4a5d6c' : '#8b9faa', color: '#ffffff' }}>{cat}</span>
                    ))}
                  </div>
                </motion.div>

                {/* Brand Text - Desktop */}
                <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6, duration: 0.8 }} className="hidden md:block mt-auto">
                  <h1 className="font-brand uppercase text-6xl md:text-8xl lg:text-9xl xl:text-[10rem] leading-none" style={{ color: slide.textColor, fontWeight: 700, letterSpacing: '-0.02em' }}>COCOONUT</h1>
                </motion.div>
              </div>

              {/* Right Image */}
              <div className="relative h-64 md:h-full">
                {slide.type === 'video' ? (
                  <video src={slide.video} poster={slide.poster} autoPlay loop muted playsInline className="w-full h-full object-cover" />
                ) : (
                  <img src={slide.image} alt={slide.title} className="w-full h-full object-cover" loading={index === 0 ? "eager" : "lazy"} />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/40 to-black/30 md:from-black/30 md:to-transparent" />
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Hero Overlay - Desktop */}
      <div className="hidden md:block absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20 text-center w-full px-6 pointer-events-none">
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 1 }}>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-semibold text-white mb-4 leading-tight">
            {tx.h1}<br />{tx.h1b}
          </h2>
          <p className="text-lg md:text-xl text-white/80 mb-3 max-w-2xl mx-auto">{tx.sub}</p>
          <p className="text-sm md:text-base text-white/60 mb-8 font-mono tracking-widest uppercase">{tx.tagline}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pointer-events-auto">
            <button onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })} className="cta-button bg-white text-[#1a3a52] hover:bg-[#f5e6d3] transition-all px-8 py-4 text-sm">{tx.cta1}</button>
            <button onClick={() => document.getElementById('portfolio').scrollIntoView({ behavior: 'smooth' })} className="cta-button bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#1a3a52] transition-all px-8 py-4 text-sm">{tx.cta2}</button>
          </div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 1 }} className="hidden md:block absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 cursor-pointer" onClick={scrollToContent}>
        <motion.div animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
          <ArrowDown className="text-white" size={32} />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
